import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    DataGridBody,
    DataGridRow,
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridCell,
    TableCellLayout,
    createTableColumn,
    Checkbox,
  } from "@fluentui/react-components";

function FeaturesPage() {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [features, setFeatures] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(true);

  useEffect(() => {
    if (!customerId) return;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers`)
      .then((res) => res.json())
      .then((data) => {
        const foundCustomer = data.find(c => c.CustomerID === parseInt(customerId, 10));
        if (foundCustomer) {
          setCustomer(foundCustomer);
        } else {
          setError('Customer not found');
        }
        setLoadingCustomer(false);
      })
      .catch((err) => {
        console.error('Failed to fetch customer:', err);
        setError('Failed to load customer information');
        setLoadingCustomer(false);
      });
  }, [customerId]);

  const fetchFeatures = useCallback((customerId) => {
    if (!customerId) {
      setFeatures([]);
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/features`)
      .then((res) => res.json())
      .then((data) => setFeatures(data))
      .catch((err) => {
        console.error('Failed to fetch features:', err);
        setFeatures([]);
      });
  }, []);

  useEffect(() => {
    if (customerId) {
      fetchFeatures(customerId);
    } else {
      setFeatures([]);
    }
  }, [customerId, fetchFeatures]);

  const handleToggleFeature = async (featureFlagId, currentValue) => {
    if (!customerId) return;

    setUpdating(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/features/${featureFlagId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ IsEnabled: !currentValue }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update feature flag');
      }
      setError(null);
      setFeatures((prevFeatures) =>
        prevFeatures.map((feature) =>
          feature.FeatureFlagID === featureFlagId
            ? { ...feature, IsEnabled: !feature.IsEnabled }
            : feature
        )
      );
    } catch (err) {
      console.error('Error updating feature flag:', err);
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const featureColumns = [
    createTableColumn({
      columnId: 'FeatureFlagID',
      compare: (a, b) => {
        return (a.FeatureFlagID || 0) - (b.FeatureFlagID || 0);
      },
      renderHeaderCell: () => 'Feature Flag ID',
      renderCell: (item) => (
        <TableCellLayout>
          {item.FeatureFlagID || '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'FeatureName',
      compare: (a, b) => {
        return (a.FeatureName || '').localeCompare(b.FeatureName || '');
      },
      renderHeaderCell: () => 'Feature Name',
      renderCell: (item) => (
        <TableCellLayout>
          {item.FeatureName || '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Description',
      compare: (a, b) => {
        return (a.Description || '').localeCompare(b.Description || '');
      },
      renderHeaderCell: () => 'Description',
      renderCell: (item) => (
        <TableCellLayout>
          {item.Description || '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Status',
      compare: (a, b) => {
        return (a.Status || '').localeCompare(b.Status || '');
      },
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <TableCellLayout>
          {item.Status || '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'CreatedDate',
      compare: (a, b) => {
        return (a.CreatedDate || '').localeCompare(b.CreatedDate || '');
      },
      renderHeaderCell: () => 'Created Date',
      renderCell: (item) => (
        <TableCellLayout>
          {item.CreatedDate ? item.CreatedDate.split('T')[0] : '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'EnabledDate',
      compare: (a, b) => {
        return (a.EnabledDate || '').localeCompare(b.EnabledDate || '');
      },
      renderHeaderCell: () => 'Enabled Date',
      renderCell: (item) => (
        <TableCellLayout>
          {item.EnabledDate ? item.EnabledDate.split('T')[0] : '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'IsGloballyEnabled',
      compare: (a, b) => {
        return (a.IsGloballyEnabled ? 1 : 0) - (b.IsGloballyEnabled ? 1 : 0);
      },
      renderHeaderCell: () => 'Is Globally Enabled',
      renderCell: (item) => (
        <TableCellLayout>
          {item.IsGloballyEnabled ? 'Yes' : 'No'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'IsEnabled',
      compare: (a, b) => {
        return (a.IsEnabled ? 1 : 0) - (b.IsEnabled ? 1 : 0);
      },
      renderHeaderCell: () => 'Is Enabled',
      renderCell: (item) => (
        <TableCellLayout>
          <Checkbox
            checked={item.IsEnabled || false}
            onChange={() => handleToggleFeature(item.FeatureFlagID, item.IsEnabled)}
            disabled={updating}
            className="feature-checkbox"
            style={{ cursor: updating ? 'not-allowed' : 'pointer' }}
          />
        </TableCellLayout>
      )
    })
  ];

  const checkboxStyles = `
    .feature-checkbox {
      cursor: pointer;
    }
    .feature-checkbox.fui-Checkbox [class*="indicator"],
    .feature-checkbox [class*="Checkbox"] [class*="indicator"] {
      transition: all 0.2s ease;
      width: 24px;
      height: 24px;
      min-width: 24px;
      min-height: 24px;
    }
    .feature-checkbox.fui-Checkbox input[type="checkbox"],
    .feature-checkbox [class*="Checkbox"] input[type="checkbox"] {
      width: 24px;
      height: 24px;
    }
    .feature-checkbox.fui-Checkbox svg,
    .feature-checkbox [class*="Checkbox"] svg {
      width: 16px;
      height: 16px;
    }
    .feature-checkbox.fui-Checkbox:has(input[type="checkbox"]:checked) [class*="indicator"],
    .feature-checkbox [class*="Checkbox"]:has(input[type="checkbox"]:checked) [class*="indicator"] {
      background-color: #0078d4;
      border-color: #0078d4;
    }
    .feature-checkbox.fui-Checkbox:hover:has(input[type="checkbox"]:checked) [class*="indicator"],
    .feature-checkbox [class*="Checkbox"]:hover:has(input[type="checkbox"]:checked) [class*="indicator"] {
      background-color: #106ebe;
      border-color: #106ebe;
    }
    .feature-checkbox.fui-Checkbox:hover:has(input[type="checkbox"]:not(:disabled):not(:checked)) [class*="indicator"],
    .feature-checkbox [class*="Checkbox"]:hover:has(input[type="checkbox"]:not(:disabled):not(:checked)) [class*="indicator"] {
      border-color: #0078d4;
      box-shadow: 0 0 0 2px rgba(0, 120, 212, 0.2);
    }
    .feature-checkbox.fui-Checkbox:has(input[type="checkbox"]:checked) svg,
    .feature-checkbox.fui-Checkbox:has(input[type="checkbox"]:checked) path,
    .feature-checkbox [class*="Checkbox"]:has(input[type="checkbox"]:checked) svg,
    .feature-checkbox [class*="Checkbox"]:has(input[type="checkbox"]:checked) path {
      fill: white;
      width: 16px;
      height: 16px;
    }
  `;

  return (
    <div style={{ margin: '20px' }}>
      <style>{checkboxStyles}</style>
      <h1>Features</h1>
      <Link 
        to="/customers" 
        style={{ 
          display: 'inline-block', 
          marginBottom: '20px', 
          padding: '8px 16px', 
          backgroundColor: '#0078d4', 
          color: 'white', 
          textDecoration: 'none', 
          borderRadius: '4px',
          fontSize: '14px'
        }}
      >
        Back to Customers
      </Link>
      {loadingCustomer ? (
        <p>Loading customer information...</p>
      ) : customer ? (
        <>
          <h2>Features for {customer.CompanyName}</h2>
          {error && (
            <div style={{ color: 'red', marginBottom: '12px' }}>
              Error: {error}
            </div>
          )}
          {features.length === 0 ? (
            <p>No features found.</p>
          ) : (
            <div className="no-clickable-rows" style={{ 
              border: '1px solid #e1dfdd', 
              borderRadius: '4px',
              overflow: 'hidden',
              marginTop: '20px'
            }}>
              <DataGrid
                items={features}
                columns={featureColumns}
                sortable
                noNativeElements={false}
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  tableLayout: 'auto'
                }}
              >
                <DataGridHeader>
                  <DataGridRow style={{ borderBottom: '2px solid #e1dfdd' }}>
                    {({ renderHeaderCell }) => (
                      <DataGridHeaderCell
                        style={{
                          borderRight: '1px solid #e1dfdd',
                          padding: '2px 4px',
                          backgroundColor: '#f3f2f1',
                          fontWeight: '600'
                        }}
                      >
                        {renderHeaderCell()}
                      </DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody>
                  {({ item, rowId }) => (
                    <DataGridRow 
                      key={rowId}
                      style={{ borderBottom: '1px solid #e1dfdd' }}
                    >
                      {({ renderCell }) => (
                        <DataGridCell
                          style={{
                            borderRight: '1px solid #e1dfdd',
                            padding: '2px 4px'
                          }}
                        >
                          {renderCell(item)}
                        </DataGridCell>
                      )}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
            </div>
          )}
        </>
      ) : (
        <p>Customer not found.</p>
      )}
    </div>
  );
}

export default FeaturesPage;
